import {Form, Formik, FormikProps} from 'formik';
import {map, pickBy, forEach, isEmpty, get, replace, filter} from 'lodash';
import {Fragment, useCallback, useContext, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {groupApi, livehelpApi, userApi} from 'api';
import {
    FilesManagerInput,
    Card,
    Button,
    InputFormik,
    Select,
    Information,
    ToggleFormik,
    notifier,
    CopyText,
    ClientInfoControls,
    ExternalLink,
    PageHeader,
    PredefinedNotifierType,
    AssignmentUserSelectorFormik,
    SelectFormik,
    DataWrapper
} from 'components';
import {FileArea, FileOwner} from 'components/files-manager/types';
import {ACTIVITY_NAME_TRANSLATE} from 'constants/session';
import {UserContext} from 'context';
import {useApiData} from 'hooks';
import {
    Group,
    LiveHelpMode,
    LiveHelpOptions,
    LiveHelpPosition,
    Option,
    SessionActivityName
} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {ApiError, UserSummary} from 'types';
import {apiUtils, userInfoUtils} from 'utils';

import {ACTIVITIES, DEFAULT_ICON_BACKGROUND} from './constants';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.LIVE_HELP;
const COMMON_SESSION_T_KEYS = TRANSLATION_KEYS.COMMON.SESSION.LABELS;

const LIVE_HELP_WIDGET_FILE_NAME = 'eazyshow-live-help.pdf';

const MODES: LiveHelpMode[] = [LiveHelpMode.Popup, LiveHelpMode.IFrame, LiveHelpMode.Widget];
const MODE_NAMES_T_KEYS = {
    [LiveHelpMode.Popup]: T_KEYS.LIST_OPTIONS.WIDGET_MODE.POPUP,
    [LiveHelpMode.IFrame]: T_KEYS.LIST_OPTIONS.WIDGET_MODE.IFRAME,
    [LiveHelpMode.Widget]: T_KEYS.LIST_OPTIONS.WIDGET_MODE.WIDGET,
};

const POSITIONS: LiveHelpPosition[] = [
    LiveHelpPosition.Inline, LiveHelpPosition.RightTop, LiveHelpPosition.RightBottom,
    LiveHelpPosition.LeftTop, LiveHelpPosition.LeftBottom
];
const POSITION_NAME_T_KEYS = {
    [LiveHelpPosition.Inline]: T_KEYS.LIST_OPTIONS.POSITION.INLINE,
    [LiveHelpPosition.RightTop]: T_KEYS.LIST_OPTIONS.POSITION.RIGHT_TOP,
    [LiveHelpPosition.RightBottom]: T_KEYS.LIST_OPTIONS.POSITION.RIGHT_BOTTOM,
    [LiveHelpPosition.LeftTop]: T_KEYS.LIST_OPTIONS.POSITION.LEFT_TOP,
    [LiveHelpPosition.LeftBottom]: T_KEYS.LIST_OPTIONS.POSITION.LEFT_BOTTOM,
};

const FIELDS = {
    USER_INFO: {
        ID: 'userInfo.id',
        FIRST_NAME: 'userInfo.firstName',
        LAST_NAME: 'userInfo.lastName'
    },
    ASSIGNMENT_INFO: 'assignmentInfo',
    OPTIONS: {
        MODE: 'options.mode',
        POSITION: 'options.position',
        CONVERSATION_ONLY: 'options.conversationOnly',
        RENDER_ON_INIT: 'options.renderOnInit',
        STYLED: 'options.styled',
        CLASS_LIST: 'options.classList',
        IFRAME: 'options.iframe',
        CONTAINER: 'options.container',
        ON_LOAD: 'options.onLoad',
        SELFIE_VALIDATION_CALLBACK: 'options.selfieValidationCallback',
        ICON_URL: 'options.iconUrl',
        INNER_ICON_URL: 'options.innerIconUrl',
        ICON_BACKGROUND: 'options.iconBackground',
        DEFAULT_ACTIVITY: 'options.defaultActivity',
        ACTIVITIES_SELECTION: 'options.activitiesSelection',
        APPOINTMENT: {
            CODE: 'options.appointment.code',
            PIN: 'options.appointment.pin'
        },
        AGENT_INFO: {
            EMAIL: 'options.agentInfo.email'
        },
        TITLE: 'options.title'
    },
    IS_APPOINTMENT: 'isAppointment'
};

const FIELD_VALIDATION_MESSAGES = {
    [FIELDS.USER_INFO.ID]: T_KEYS.ERRORS.USER_ID,
    [FIELDS.USER_INFO.FIRST_NAME]: T_KEYS.ERRORS.USER_FIRST_NAME,
    [FIELDS.USER_INFO.LAST_NAME]: T_KEYS.ERRORS.USER_LAST_NAME,
    [FIELDS.OPTIONS.ICON_URL]: T_KEYS.ERRORS.ICON_URL,
    [FIELDS.OPTIONS.INNER_ICON_URL]: T_KEYS.ERRORS.INNER_ICON_URL
};

type UserInfo = {
    controlOnlyInfo?: {
        [key: string]: string
    }
} & { [key: string]: any }

type FormFields = {
    userInfo: UserInfo,
    assignmentInfo: {
        groupId: string,
        subgroupId: string
    },
    options: {
        mode: {id: LiveHelpMode, name: string},
        position: {id: LiveHelpPosition, name: string},
        conversationOnly: boolean,
        renderOnInit: boolean,
        styled: boolean,
        classList: string,
        iframe: string,
        container: string,
        onLoad: string,
        selfieValidationCallback: string,
        iconUrl: string,
        innerIconUrl: string,
        iconBackground: string,
        defaultActivity: {id: SessionActivityName, name: string}
        activitiesSelection: {id: SessionActivityName, name: string}[],
        appointment: {pin: string, code: string},
        agentInfo: {
            email: string
        },
        title: string
    },
    isAppointment: boolean
}

type FormTemplateProps = {
    error: ApiError;
    isLoading: boolean;
    isInitialLoading: boolean;
    groups: Group[];
    users?: UserSummary[];
    activityNames: SessionActivityName[];
} & FormikProps<FormFields>;

const FormTemplate: React.FC<FormTemplateProps> = ({
    error, errors, isLoading, isInitialLoading, values, setFieldValue, setFieldError,
    groups, users, activityNames
}) => {
    const {t} = useTranslation();
    const modeOptions: Option[] = useMemo(() => map(MODES, (mode) => ({id: mode, name: t(MODE_NAMES_T_KEYS[mode])})), [t]);
    const positionOptions: Option[] = useMemo(() => map(POSITIONS, (pos) => ({id: pos, name: t(POSITION_NAME_T_KEYS[pos])})), [t]);
    const activityOptions: Option[] = useMemo(() =>
        map(activityNames, (activity) => ({id: activity, name: t(ACTIVITY_NAME_TRANSLATE[activity])})), [t, activityNames]);

    const onModeUpdated = useCallback(({id}: Option) => {
        const mode = id as LiveHelpMode;

        setFieldValue(FIELDS.OPTIONS.MODE, {id: mode, name: t(MODE_NAMES_T_KEYS[mode])});
        setFieldValue(FIELDS.OPTIONS.IFRAME, id === LiveHelpMode.IFrame ? '#iframe' : '');

        if (id === LiveHelpMode.Widget) {
            setFieldValue(FIELDS.OPTIONS.CONVERSATION_ONLY, false);
        }

        if (id !== LiveHelpMode.Widget) {
            setFieldValue(FIELDS.OPTIONS.DEFAULT_ACTIVITY, null);
            setFieldValue(FIELDS.OPTIONS.ACTIVITIES_SELECTION, []);
            setFieldValue(FIELDS.OPTIONS.APPOINTMENT.PIN, null);
            setFieldValue(FIELDS.OPTIONS.APPOINTMENT.CODE, null);
            setFieldValue(FIELDS.IS_APPOINTMENT, false);
            setFieldValue(FIELDS.OPTIONS.TITLE, t(T_KEYS.DEFAULT_VALUES.TITLE));
        }
    }, [setFieldValue, t]);

    const onIconUrlUpdate = useCallback((field, value) => {
        setFieldValue(field, value);
        setFieldError(field, null);

        if (field === FIELDS.OPTIONS.ICON_URL && value) {
            setFieldValue(FIELDS.OPTIONS.INNER_ICON_URL, '');
            setFieldValue(FIELDS.OPTIONS.ICON_BACKGROUND, '');
        }

        if ((field === FIELDS.OPTIONS.INNER_ICON_URL || field === FIELDS.OPTIONS.ICON_BACKGROUND) && value) {
            setFieldValue(FIELDS.OPTIONS.ICON_URL, '');
        }
    }, [setFieldValue, setFieldError]);

    useEffect(() => {
        onIconUrlUpdate(FIELDS.OPTIONS.ICON_BACKGROUND, values?.options?.iconBackground);
    }, [values?.options?.iconBackground, onIconUrlUpdate]);

    const onPositionUpdated = useCallback(({id}: Option) => {
        const position = id as LiveHelpPosition;
        setFieldValue(FIELDS.OPTIONS.POSITION, {id: position, name: t(POSITION_NAME_T_KEYS[position])});
        setFieldValue(FIELDS.OPTIONS.CONTAINER, id === LiveHelpPosition.Inline ? '#container' : '');
    }, [setFieldValue, t]);

    useEffect(() => {
        const _errors = userInfoUtils.getUserInfoErrors(error, t);

        forEach(_errors, (value, key) => {
            setFieldError(key, value);
        });
    }, [error, setFieldError, t]);

    const iconBackgroundDescription = useMemo(() =>
        `${t(T_KEYS.OPTION_INLINE_DESCRIPTIONS.ICON_BACKGROUND)} ${DEFAULT_ICON_BACKGROUND}`, [t]);

    return (
        <Form className='tw-m-4'>
            <DataWrapper isLoading={isLoading} hideContentOnLoading={isInitialLoading}>
                <div>
                    <div className='tw-ts-h5-bold tw-mb-4'>{t(T_KEYS.SECTION_NAMES.ASSIGNMENT_INFO)}</div>
                    <div className='tw-grid tw-mb-8 tw-gap-4 -md:tw-flex-col'>
                        <AssignmentUserSelectorFormik
                            assignmentSelectorProps={{
                                groups,
                                name: FIELDS.ASSIGNMENT_INFO,
                                grid: true
                            }}
                            userSelectorProps={{
                                users,
                                name: FIELDS.OPTIONS.AGENT_INFO.EMAIL,
                                context: 'agent',
                                field: 'email'
                            }}
                        />
                    </div>

                    <div className='tw-ts-h5-bold tw-mb-4'>{t(T_KEYS.SECTION_NAMES.USER_INFO)}</div>
                    <ClientInfoControls assignment={values.assignmentInfo} hideTitle />
                </div>
                <div>
                    <div className='tw-ts-h5-bold tw-mb-4'>{t(T_KEYS.SECTION_NAMES.OPTIONS)}</div>
                    <div className='tw-grid md:tw-grid-cols-3 tw-gap-4 tw-mb-8'>
                        <Information
                            header={t(T_KEYS.FORM_CONTROL_LABELS.MODE)}
                            message={t(T_KEYS.OPTION_DESCRIPTIONS.MODE)}
                        >
                            <Select
                                value={values.options.mode}
                                placeholder={t(T_KEYS.FORM_CONTROL_LABELS.MODE)}
                                options={modeOptions}
                                className='tw-w-full'
                                onChange={onModeUpdated}
                            />
                        </Information>

                        {values.options.mode.id === LiveHelpMode.IFrame &&
                            <Information
                                className='tw-mt-4'
                                header={t(T_KEYS.FORM_CONTROL_LABELS.IFRAME)}
                                message={t(T_KEYS.OPTION_DESCRIPTIONS.IFRAME)}
                            >
                                <InputFormik
                                    name='options.iframe'
                                    label={t(T_KEYS.FORM_CONTROL_LABELS.IFRAME)}
                                    className='tw-w-full'
                                />
                            </Information>}
                        <Information
                            header={t(T_KEYS.FORM_CONTROL_LABELS.POSITION)}
                            message={t(T_KEYS.OPTION_DESCRIPTIONS.POSITION)}
                        >
                            <Select
                                value={values.options.position}
                                placeholder={t(T_KEYS.FORM_CONTROL_LABELS.POSITION)}
                                options={positionOptions}
                                className='tw-w-full'
                                onChange={onPositionUpdated}
                            />
                        </Information>
                        {values.options.position.id === LiveHelpPosition.Inline &&
                            <Information
                                className='tw-mt-4'
                                header={t(T_KEYS.FORM_CONTROL_LABELS.CONTAINER)}
                                message={t(T_KEYS.OPTION_DESCRIPTIONS.CONTAINER)}
                            >
                                <InputFormik
                                    name='options.container'
                                    label={t(T_KEYS.FORM_CONTROL_LABELS.CONTAINER)}
                                    className='tw-w-full'
                                />
                            </Information>}
                        <Information
                            className='tw-mt-4'
                            header={t(T_KEYS.FORM_CONTROL_LABELS.ICON_URL)}
                            message={t(T_KEYS.OPTION_DESCRIPTIONS.ICON_URL)}
                        >
                            <FilesManagerInput
                                filesManagerProps={{
                                    ownerType: FileOwner.Account,
                                    area: FileArea.LiveHelp
                                }}
                                inputProps={{
                                    name: 'icon-url',
                                    label: t(T_KEYS.FORM_CONTROL_LABELS.ICON_URL),
                                    description: t(T_KEYS.OPTION_INLINE_DESCRIPTIONS.ICON_URL),
                                    value: values.options.iconUrl,
                                    hasError: get(errors, FIELDS.OPTIONS.ICON_URL),
                                    errorMessage: t(FIELD_VALIDATION_MESSAGES[FIELDS.OPTIONS.ICON_URL]),
                                    onChange: (value) => onIconUrlUpdate(FIELDS.OPTIONS.ICON_URL, value)
                                }}
                                onFilePathSelect={(path) => onIconUrlUpdate(FIELDS.OPTIONS.ICON_URL, path)}
                            />
                        </Information>
                        <Information
                            className='tw-mt-4'
                            header={t(T_KEYS.FORM_CONTROL_LABELS.INNER_ICON_URL)}
                            message={t(T_KEYS.OPTION_DESCRIPTIONS.INNER_ICON_URL)}
                        >
                            <FilesManagerInput
                                filesManagerProps={{
                                    ownerType: FileOwner.Account,
                                    area: FileArea.LiveHelp
                                }}
                                inputProps={{
                                    name: 'inner-icon-url',
                                    label: t(T_KEYS.FORM_CONTROL_LABELS.INNER_ICON_URL),
                                    value: values.options.innerIconUrl,
                                    hasError: get(errors, FIELDS.OPTIONS.INNER_ICON_URL),
                                    errorMessage: t(FIELD_VALIDATION_MESSAGES[FIELDS.OPTIONS.INNER_ICON_URL]),
                                    onChange: (value) => onIconUrlUpdate(FIELDS.OPTIONS.INNER_ICON_URL, value)
                                }}
                                onFilePathSelect={(path) => onIconUrlUpdate(FIELDS.OPTIONS.INNER_ICON_URL, path)}
                            />
                        </Information>
                        <Information
                            className='tw-mt-4'
                            header={t(T_KEYS.FORM_CONTROL_LABELS.ICON_BACKGROUND)}
                            message={t(T_KEYS.OPTION_DESCRIPTIONS.ICON_BACKGROUND)}
                        >
                            <InputFormik
                                name='options.iconBackground'
                                className='tw-w-full'
                                label={t(T_KEYS.FORM_CONTROL_LABELS.ICON_BACKGROUND)}
                                description={iconBackgroundDescription}
                            />
                        </Information>
                        {values.options.mode.id !== LiveHelpMode.Widget &&
                            <div className='tw-mt-4'>
                                <Information
                                    header={t(T_KEYS.FORM_CONTROL_LABELS.CONVERSATION_ONLY)}
                                    message={t(T_KEYS.OPTION_DESCRIPTIONS.CONVERSATION_ONLY)}
                                >
                                    <div className='tw-flex tw-items-center tw-justify-between tw-w-full tw-ml-3'>
                                        <div>{t(T_KEYS.FORM_CONTROL_LABELS.CONVERSATION_ONLY)}</div>
                                        <ToggleFormik name={FIELDS.OPTIONS.CONVERSATION_ONLY} />
                                    </div>
                                </Information>
                            </div>}
                        <div className='tw-mt-4'>
                            <Information
                                header={t(T_KEYS.FORM_CONTROL_LABELS.RENDER_ON_INIT)}
                                message={t(T_KEYS.OPTION_DESCRIPTIONS.RENDER_ON_INIT)}
                            >
                                <div className='tw-flex tw-items-center tw-justify-between tw-w-full tw-ml-3'>
                                    <div>{t(T_KEYS.FORM_CONTROL_LABELS.RENDER_ON_INIT)}</div>
                                    <ToggleFormik name={FIELDS.OPTIONS.RENDER_ON_INIT} />
                                </div>
                            </Information>
                        </div>
                        <div className='tw-mt-4'>
                            <Information
                                header={t(T_KEYS.FORM_CONTROL_LABELS.STYLED)}
                                message={t(T_KEYS.OPTION_DESCRIPTIONS.STYLED)}
                            >
                                <div className='tw-flex tw-items-center tw-justify-between tw-w-full tw-ml-3'>
                                    <div>{t(T_KEYS.FORM_CONTROL_LABELS.STYLED)}</div>
                                    <ToggleFormik name={FIELDS.OPTIONS.STYLED} />
                                </div>
                            </Information>
                        </div>

                        <Information
                            className='tw-mt-4'
                            header={t(T_KEYS.FORM_CONTROL_LABELS.CLASS_LIST)}
                            message={t(T_KEYS.OPTION_DESCRIPTIONS.CLASS_LIST)}
                        >
                            <InputFormik
                                name='options.classList'
                                className='tw-w-full'
                                label={t(T_KEYS.FORM_CONTROL_LABELS.CLASS_LIST)}
                            />
                        </Information>

                        <Information
                            className='tw-mt-4'
                            header={t(T_KEYS.FORM_CONTROL_LABELS.ON_LOAD)}
                            message={t(T_KEYS.OPTION_DESCRIPTIONS.ON_LOAD)}
                        >
                            <InputFormik
                                name='options.onLoad'
                                className='tw-w-full'
                                label={t(T_KEYS.FORM_CONTROL_LABELS.ON_LOAD)}
                            />
                        </Information>
                        <Information
                            className='tw-mt-4'
                            header={t(T_KEYS.FORM_CONTROL_LABELS.SELFIE_VALIDATION_CALLBACK)}
                            message={t(T_KEYS.OPTION_DESCRIPTIONS.SELFIE_VALIDATION_CALLBACK)}
                        >
                            <InputFormik
                                name='options.selfieValidationCallback'
                                className='tw-w-full'
                                label={t(T_KEYS.FORM_CONTROL_LABELS.SELFIE_VALIDATION_CALLBACK)}
                            />
                        </Information>
                        {values.options.mode.id === LiveHelpMode.Widget &&
                            <Fragment>
                                <Information
                                    className='tw-mt-4'
                                    header={t(T_KEYS.FORM_CONTROL_LABELS.DEFAULT_ACTIVITY)}
                                    message={t(T_KEYS.OPTION_DESCRIPTIONS.DEFAULT_ACTIVITY)}
                                >
                                    <SelectFormik
                                        name={FIELDS.OPTIONS.DEFAULT_ACTIVITY}
                                        placeholder={t(T_KEYS.FORM_CONTROL_LABELS.DEFAULT_ACTIVITY)}
                                        options={activityOptions}
                                        className='tw-w-full'
                                        noneOption
                                    />
                                </Information>
                                <Information
                                    className='tw-mt-4'
                                    header={t(T_KEYS.FORM_CONTROL_LABELS.ACTIVITIES_SELECTION)}
                                    message={t(T_KEYS.OPTION_DESCRIPTIONS.ACTIVITIES_SELECTION)}
                                >
                                    <SelectFormik
                                        name={FIELDS.OPTIONS.ACTIVITIES_SELECTION}
                                        placeholder={t(T_KEYS.FORM_CONTROL_LABELS.ACTIVITIES_SELECTION)}
                                        options={activityOptions}
                                        className='tw-w-full'
                                        multiple
                                    />
                                </Information>
                                <div className='tw-mt-4'>
                                    <Information
                                        header={t(T_KEYS.FORM_CONTROL_LABELS.IS_APPOINTMENT)}
                                        message={t(T_KEYS.OPTION_DESCRIPTIONS.IS_APPOINTMENT)}
                                    >
                                        <div className='tw-flex tw-items-center tw-justify-between tw-w-full tw-ml-3'>
                                            <div>{t(T_KEYS.FORM_CONTROL_LABELS.IS_APPOINTMENT)}</div>
                                            <ToggleFormik name={FIELDS.IS_APPOINTMENT} />
                                        </div>
                                    </Information>
                                </div>
                                {values.isAppointment &&
                                    <Fragment>
                                        <Information
                                            className='tw-mt-4'
                                            header={t(COMMON_SESSION_T_KEYS.CODE)}
                                            message={t(T_KEYS.OPTION_DESCRIPTIONS.CODE)}
                                        >
                                            <InputFormik
                                                name={FIELDS.OPTIONS.APPOINTMENT.CODE}
                                                className='tw-w-full'
                                                label={t(COMMON_SESSION_T_KEYS.CODE)}
                                            />
                                        </Information>
                                        <Information
                                            className='tw-mt-4'
                                            header={t(COMMON_SESSION_T_KEYS.PIN)}
                                            message={t(T_KEYS.OPTION_DESCRIPTIONS.PIN)}
                                        >
                                            <InputFormik
                                                name={FIELDS.OPTIONS.APPOINTMENT.PIN}
                                                className='tw-w-full'
                                                label={t(COMMON_SESSION_T_KEYS.PIN)}
                                            />
                                        </Information>
                                    </Fragment>}
                                <Information
                                    className='tw-mt-4'
                                    header={t(T_KEYS.FORM_CONTROL_LABELS.TITLE)}
                                    message={t(T_KEYS.OPTION_DESCRIPTIONS.TITLE)}
                                >
                                    <InputFormik
                                        name={FIELDS.OPTIONS.TITLE}
                                        className='tw-w-full'
                                        label={t(T_KEYS.FORM_CONTROL_LABELS.TITLE)}
                                    />
                                </Information>
                            </Fragment>}
                    </div>
                </div>
            </DataWrapper>
            <div className='tw-text-center lg:tw-text-right'>
                <Button
                    loading={isLoading}
                    disabled={!isEmpty(errors)}
                    type='submit'
                    color='primary'
                    width='large'
                >
                    {t(T_KEYS.BUTTONS.GENERATE)}
                </Button>
            </div>
        </Form>
    );
};

export const LiveHelp = () => {
    const {t} = useTranslation();

    const {settings} = useContext(UserContext);

    const activityNames = useMemo(() =>
        filter(ACTIVITIES, (activityName) => (settings.featuresAvailability?.avatar || activityName !== SessionActivityName.Avatar)),
    [settings]);

    const initialValues: FormFields = useMemo(() => ({
        assignmentInfo: {
            groupId: '',
            subgroupId: ''
        },
        userInfo: userInfoUtils.generateEmpty(),
        options: {
            mode: {id: LiveHelpMode.Widget, name: t(MODE_NAMES_T_KEYS[LiveHelpMode.Widget])},
            position: {id: LiveHelpPosition.RightBottom, name: t(POSITION_NAME_T_KEYS[LiveHelpPosition.RightBottom])},
            conversationOnly: false,
            renderOnInit: true,
            styled: true,
            classList: '',
            iframe: '',
            container: '',
            onLoad: '',
            selfieValidationCallback: '',
            iconUrl: '',
            innerIconUrl: '',
            iconBackground: '',
            defaultActivity: null,
            activitiesSelection: [],
            appointment: {pin: '', code: ''},
            agentInfo: {
                email: ''
            },
            title: t(T_KEYS.DEFAULT_VALUES.TITLE)
        },
        isAppointment: false
    }), [t]);

    const formatOptions = useCallback((options: FormFields['options'], isAppointment: boolean): LiveHelpOptions => ({
        ...options,
        mode: options.mode.id,
        position: options.position.id,
        defaultActivity: options.defaultActivity?.id || null,
        activitiesSelection: options.activitiesSelection.map((activity) => activity.id),
        appointment: isAppointment ? options.appointment : null
    }), []);

    const generateScriptFn = useCallback(({assignment, userInfo, options, isAppointment}) => {
        const formattedOptions = formatOptions(options, isAppointment);
        return livehelpApi.generateIntegration({assignment, userInfo, options: formattedOptions});
    }, [formatOptions]);

    const {
        send: generateScript,
        loading,
        data: integration,
        error
    } = useApiData(generateScriptFn, {onError: () => notifier.error({title: t(T_KEYS.ERRORS.GENERATE_FAILED)})});

    // for local testing purposes
    const integrationUrl = useMemo(() => replace(integration?.url, 'localhost:3001', 'localhost:8081'), [integration?.url]);

    const onDownloadSuccess = useCallback((data) =>
        apiUtils.saveFileFromBase64(LIVE_HELP_WIDGET_FILE_NAME, data), []);

    const {
        send: downloadDocumentation,
        loading: loadingDocumentation,
    } = useApiData(livehelpApi.downloadDocumentation, {onSuccess: onDownloadSuccess, notificationType: PredefinedNotifierType.FetchError});

    const {send: loadGroups, data: groups, loading: groupsLoading} = useApiData(groupApi.getGroups);
    const {send: loadAgentUsers, data: agentUsers, loading: agentUsersLoading} = useApiData<UserSummary[]>(userApi.getAgentUsersSummary);

    const isInitialLoading = useMemo(() => agentUsersLoading || groupsLoading, [groupsLoading, agentUsersLoading]);

    useEffect(() => {
        loadGroups();
        loadAgentUsers();
    }, [loadGroups, loadAgentUsers]);

    const handleSubmit = useCallback((formValues: FormFields) => {
        const data = {
            assignment: pickBy(formValues.assignmentInfo, Boolean),
            userInfo: userInfoUtils.format(formValues.userInfo),
            options: pickBy(formValues.options, Boolean),
            isAppointment: formValues.isAppointment
        };
        return generateScript(data);
    }, [generateScript]);

    return (
        <Fragment>
            <PageHeader title={t(T_KEYS.TITLE)} description={t(T_KEYS.DESCRIPTION)} />
            <Button
                className='tw-my-6'
                icon='arrow-down'
                width='large'
                disabled={loadingDocumentation}
                onClick={downloadDocumentation}
            >
                {t(T_KEYS.BUTTONS.DOWNLOAD_DOCUMENTATION)}
            </Button>
            <Card className='tw-relative'>
                <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    onSubmit={handleSubmit}
                >
                    {(props) => (
                        <FormTemplate
                            {...props}
                            activityNames={activityNames}
                            groups={groups}
                            users={agentUsers}
                            error={error}
                            isInitialLoading={isInitialLoading}
                            isLoading={loading || isInitialLoading}
                        />
                    )}
                </Formik>
            </Card>

            {integration?.script &&
                <Card className='tw-mt-4'>
                    <div className='tw-m-4'>
                        <div className='tw-ts-h5-bold'>{t(T_KEYS.FORM_CONTROL_LABELS.SCRIPT)}</div>
                        <p className='tw-my-4'>{t(T_KEYS.OPTION_DESCRIPTIONS.SCRIPT)}</p>

                        <div className='tw-bg-layout tw-border tw-border-default tw-h-32 tw-px-4 tw-py-2 tw-mb-6
                        tw-overflow-y-auto tw-text-default'
                        >
                            {integration.script}
                        </div>
                        <div className='tw-text-center lg:tw-text-right'>
                            <CopyText
                                copiedLabelOnly
                                text={integration?.script}
                                textInTooltip={false}
                            >
                                <Button
                                    icon='copy'
                                    color='primary'
                                    width='large'
                                >
                                    {t(T_KEYS.BUTTONS.COPY)}
                                </Button>
                            </CopyText>
                        </div>
                    </div>
                </Card>}

            {integration?.url &&
                <Card className='tw-mt-4'>
                    <div className='tw-m-4'>
                        <div className='tw-ts-h5-bold'>{t(T_KEYS.FORM_CONTROL_LABELS.GENERATED_URL)}</div>
                        <p className='tw-my-4'>{t(T_KEYS.OPTION_DESCRIPTIONS.GENERATED_URL)}</p>

                        <div className='tw-bg-layout tw-border tw-border-default tw-h-32 tw-px-4 tw-py-2 tw-mb-6
                        tw-overflow-y-auto tw-text-default tw-break-all'
                        >
                            {integrationUrl}
                        </div>
                        <div className='tw-text-center lg:tw-text-right'>
                            <ExternalLink link={integrationUrl} className='tw-ml-3 tw-mt-3'>
                                <Button icon='external-link' width='large'>{t(T_KEYS.BUTTONS.OPEN_LINK)}</Button>
                            </ExternalLink>

                            <CopyText
                                copiedLabelOnly
                                text={integrationUrl}
                                textInTooltip={false}
                                className='tw-ml-3 tw-mt-3'
                            >
                                <Button
                                    icon='copy'
                                    color='primary'
                                    width='large'
                                >
                                    {t(T_KEYS.BUTTONS.COPY_LINK)}
                                </Button>
                            </CopyText>
                        </div>
                    </div>
                </Card>}
        </Fragment>
    );
};
